import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { I18NService, UserService } from '@socrates/socrates-shared';
import { AccountOnboardingState } from '@socrates/common';

@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	standalone: false,
})
export class ProfileComponent implements OnInit {
	readonly loading    = signal(true);
	readonly saving     = signal(false);
	readonly saveError  = signal<string | null>(null);
	readonly successMsg = signal<string | null>(null);

	profileForm: FormGroup;

	readonly timezones: string[] = [
		'Pacific/Midway','Pacific/Honolulu','America/Anchorage','America/Los_Angeles',
		'America/Denver','America/Chicago','America/New_York','America/Halifax',
		'America/St_Johns','America/Sao_Paulo','Atlantic/South_Georgia','Atlantic/Azores',
		'UTC','Europe/London','Europe/Paris','Europe/Helsinki','Europe/Moscow',
		'Asia/Dubai','Asia/Karachi','Asia/Dhaka','Asia/Bangkok','Asia/Singapore',
		'Asia/Tokyo','Australia/Sydney','Pacific/Auckland','Pacific/Fiji',
	];

	constructor(
		private fb: FormBuilder,
		private userService: UserService,
		private router: Router,
	) {}

	async ngOnInit(): Promise<void> {
		this.profileForm = this.fb.group({
			firstName:         ['', Validators.required],
			lastName:          ['', Validators.required],
			email:             [{ value: '', disabled: true }],
			timezone:          [''],
			notifyFloorEvents:  [true],
			notifyObjections:   [true],
			notifyDecisions:    [true],
			notifyDiscussion:   [true],
		});

		try {
			const profile = await this.userService.getOwnProfile();
			this.profileForm.patchValue(profile);
		} catch {
			this.saveError.set(I18NService.getMessage('profile.error.load'));
		} finally {
			this.loading.set(false);
		}
	}

	async onSubmit(): Promise<void> {
		this.saveError.set(null);
		this.successMsg.set(null);
		if (this.profileForm.invalid) {
			this.profileForm.markAllAsTouched();
			return;
		}
		this.saving.set(true);
		try {
			const { firstName, lastName, timezone, notifyFloorEvents, notifyObjections, notifyDecisions, notifyDiscussion } = this.profileForm.value;
			await this.userService.updateProfile({ firstName, lastName, timezone, notifyFloorEvents, notifyObjections, notifyDecisions, notifyDiscussion });
			this.successMsg.set(I18NService.getMessage('profile.saved'));
		} catch (err: any) {
			this.saveError.set(err?.error?.error ?? I18NService.getMessage('profile.error.save'));
		} finally {
			this.saving.set(false);
		}
	}

	onCancel(): void {
		this.router.navigate(['/dashboard']);
	}

	get isActiveMember(): boolean {
		return this.userService.user?.accountState === AccountOnboardingState.Active;
	}

	getMessage(key: string): string {
		return I18NService.getMessage(key);
	}
}
